import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  Paper,
  Avatar,
  useMediaQuery,
} from '@mui/material';
import { useHistory } from 'react-router-dom';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import { RecordVoiceOver } from '@mui/icons-material';
import {
  answerSurveyQuestion,
  getActiveSurvey,
  getSurveyResponses,
} from './../../services/survey_services';
import {
  getCompanyPreviewPosts,
  requestToSpeak,
} from './../../services/advisor_services';
import { useAccount } from '../../contexts/Account';
import eventBus, { eventBusValues } from '../../eventBus';
import DefaultCompany from './../../icons/building_round.svg';
import calendarcheck from './../../icons/calendar-check.svg';
import { extractUrl } from './../../utils/urlUtils';
import { convertTimezoneToAbbr } from './../../utils/dateFormat';
import { addActivityFromAnswerSurvey } from '../../services/utils/surveys';
import ShareYourIdeasDialog from '../../components/BoardroomActivity/ShareYourIdeasDialog';
import SurveyStats from '../../components/BoardroomActivityCard/SurveyStats';
import ConfirmRequestToSpeakDialog from '../../scenes/CompanyDashboard/ConfirmRequestToSpeakDialog';

const classes = {
  surveyButton: {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '14px',
    width: { xxs: '100%', xs: '140px' },
    height: '36px',
    borderRadius: '10px',
    margin: '0px',
  },
};

export default function CurrentPriorities({
  company,
  seeOpportunity,
  openProfile,
}) {
  const [surveyAnswer, setSurveyAnswer] = useState(null);
  const [oldAnswerBody, setOldAnswerBody] = useState(null);
  const [singleQuestion, setSingleQuestion] = useState(null);
  const [nextMeeting, setNextMeeting] = useState(null);
  const [time, setTime] = useState(null);
  const [loading, setLoading] = useState(false);
  const [shareYourIdeasDialogOpen, setShareYourIdeasDialogOpen] =
    useState(false);
  const { accountDetails, isFreeTrial, isFreemium, type } = useAccount();
  const isOwnProfile = company.id === accountDetails.id;
  const isSM = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const localZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const formattedLocalMeetingTime = convertTimezoneToAbbr(localZone);
  const [formattedLocalDateTime, setFormattedLocalDateTime] = useState(null);
  const [openRequestToSpeakDialog, setOpenRequestToSpeakDialog] =
    useState(false);
  const [requestToSpeakLoading, setRequestToSpeakLoading] = useState(false);
  const history = useHistory();

  useEffect(async () => {
    if (company && company.id) {
      const { data } = await getActiveSurvey({
        COMPANY_ID: company.id,
      });
      const questions = data.getActiveSurvey;
      if (questions && questions.length > 0) {
        setSingleQuestion(questions[0]);
        const response = await getSurveyResponses({
          USER_ID: accountDetails.id,
          SURVEY_ID: questions[0].surveyId,
        });

        if (response && response.data && response.data.getSurveyResponses) {
          const surveyResponses = response.data.getSurveyResponses;
          if (surveyResponses && surveyResponses.length > 0) {
            setSurveyAnswer(surveyResponses[0].body);
            setOldAnswerBody(surveyResponses[0].body);
          }
        }
      } else {
        setSingleQuestion(null);
      }
    }
  }, [company]);

  const handleRequestToSpeak = async (event) => {
    event.stopPropagation();
    if (isFreeTrial) {
      eventBus.dispatch(eventBusValues.triggerAdvisorUpgradeAction);
      return;
    }
    setRequestToSpeakLoading(true);
    try {
      const boardOwner = company.contacts.find(
        (contact) => contact.isBoardOwner
      );
      await requestToSpeak({
        advisorName: accountDetails.displayName,
        advisorEmail: accountDetails.email,
        salesforceAdvisorContactId: accountDetails.salesforceContactId || '',
        companyName: company.companyName,
        boardOwnerEmail: boardOwner ? boardOwner.email : '',
        salesforceCompanyContactId: company.salesforceContactId,
      });

      setOpenRequestToSpeakDialog(true);
    } catch (error) {
      console.log(error);
    } finally {
      setRequestToSpeakLoading(false);
    }
  };

  useEffect(() => {
    async function getPosts() {
      getCompanyPreviewPosts({ COMPANY_ID: company.id }).then((response) => {
        if (
          response.data &&
          response.data.getCompanyPreviewPosts &&
          response.data.getCompanyPreviewPosts.posts &&
          response.data.getCompanyPreviewPosts.posts.length
        ) {
          const now = new Date();

          const parsedMeetings = response.data.getCompanyPreviewPosts.posts.map(
            (m) => ({
              ...m,
              meetingTime: new Date(m.meetingTime),
            })
          );
          console.log('parsedMeetings', parsedMeetings);

          const futureMeetings = parsedMeetings.filter(
            (m) => m.meetingTime >= now
          );
          const pastMeetings = parsedMeetings.filter(
            (m) => m.meetingTime < now
          );

          console.log('futureMeetings', futureMeetings);
          console.log('pastMeetings', pastMeetings);

          if (futureMeetings.length > 0) {
            const bnm = futureMeetings.reduce((closest, current) =>
              current.meetingTime < closest.meetingTime ? current : closest
            );
            setNextMeeting(bnm);
            setTimeStatus(bnm.meetingTime);
            setFormattedLocalDateTime(formatDateTime(bnm.meetingTime));
          } else if (pastMeetings.length > 0) {
            const bnm = pastMeetings.reduce((latest, current) =>
              current.meetingTime > latest.meetingTime ? current : latest
            );
            setNextMeeting(bnm);
            setTimeStatus(bnm.meetingTime);
            setFormattedLocalDateTime(formatDateTime(bnm.meetingTime));
          }
        }
      });
    }
    getPosts();
  }, [company]);

  function setTimeStatus(localeHour) {
    function differenceInHours(dt2, dt1) {
      let diff = (dt2.getTime() - dt1.getTime()) / 1000;
      diff /= 60 * 60;
      return diff;
    }
    const today = new Date();
    setTime(
      localeHour > today
        ? 'Upcoming'
        : differenceInHours(today, localeHour) < 1
        ? 'Live'
        : 'Replay'
    );
  }

  function formatDateTime(dateString) {
    const dateObj = new Date(dateString);
    const optionsDate = { month: 'long', day: 'numeric' };
    const optionsTime = { hour: 'numeric', minute: 'numeric' };
    const datePart = dateObj.toLocaleDateString('en-US', optionsDate);
    const timePart = dateObj.toLocaleTimeString('en-US', optionsTime);

    return `${datePart} at ${timePart}`;
  }

  useEffect(() => {
    eventBus.on(eventBusValues.surveyAnswered, (activity) => {
      if (activity?.survey?.response) {
        setSurveyAnswer(activity.survey.response);
        setOldAnswerBody(activity.survey.response);
      }
    });

    return () => {
      eventBus.remove(eventBusValues.surveyAnswered);
    };
  }, []);

  async function handleSurveyResponse(surveyResponse) {
    setLoading(true);
    if (singleQuestion && oldAnswerBody === surveyResponse) {
      // console.log('Same survey answer, skipping update');
      return;
    }
    await answerSurveyQuestion({
      BODY: surveyResponse,
      SURVEY_ID: singleQuestion.surveyId,
      SURVEY_QUESTION_ID: singleQuestion.id,
      USER_ACCOUNT_TYPE: type,
      USER_ID: accountDetails.id,
    });
    setLoading(false);
    setOldAnswerBody(surveyResponse);
    if (oldAnswerBody) {
      return; // If oldAnswerBody is set, we assume this is an update to an existing answer
    }
    addActivityFromAnswerSurvey({
      accountDetails,
      surveyAnswer: surveyResponse,
      questionBody: singleQuestion.body,
    });
  }

  function openProfile(company) {
    eventBus.dispatch(eventBusValues.triggerCompanyDrawerAction, {
      company,
    });
  }
  return (
    <Box sx={{ height: '100%' }}>
      <Paper
        elevation={1}
        sx={{
          height: '100%',
          py: isSM ? '22px' : '32px',
          px: '20px',
          borderRadius: '12px',
          width: '100%',
          boxShadow: 'none',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            mb: 2,
          }}
        >
          <Avatar
            alt={`${company.companyName} Logo`}
            src={
              company &&
              company.image &&
              company.image.id &&
              !company.image.id.includes('undefined')
                ? `${process.env.REACT_APP_IMAGES_URL}${company.image.id}`
                : DefaultCompany
            }
            sx={{
              width: 32,
              height: 32,
              mt: '-4px',
            }}
            onClick={() =>
              isOwnProfile
                ? openProfile(accountDetails)
                : seeOpportunity(company.id)
            }
          />
          <Typography
            sx={{
              mb: 2,
              fontWeight: '600',
              fontSize: '20px',
              lineHeight: '130%',
              color: '#001030',
              ml: '20px',
            }}
          >
            Current Priorities
          </Typography>
        </Box>
        <Stack spacing={2}>
          {singleQuestion && (
            <Card
              variant="outlined"
              sx={{ borderRadius: 2, bgcolor: '#fafcff' }}
            >
              <CardContent>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: '600',
                      fontSize: 18,
                      lineHeight: '24px',
                      color: '#232B35',
                    }}
                  >
                    Survey
                  </Typography>
                  <Box
                    sx={{
                      width: '100%',
                      display: { xxs: 'none', xs: 'flex' },
                      flexDirection: { xxs: 'column', xs: 'row' },
                      justifyContent: 'right',
                      gap: '8px',
                      // mt: '0px',
                    }}
                  >
                    <Button
                      variant={
                        surveyAnswer === 'Yes' ? 'contained' : 'outlined'
                      }
                      color="primary"
                      onClick={() => {
                        setSurveyAnswer('Yes');
                        handleSurveyResponse('Yes');
                      }}
                      sx={classes.surveyButton}
                      disabled={loading}
                    >
                      Yes
                    </Button>
                    <Button
                      variant={surveyAnswer === 'No' ? 'contained' : 'outlined'}
                      color="primary"
                      onClick={() => {
                        setSurveyAnswer('No');
                        handleSurveyResponse('No');
                      }}
                      sx={classes.surveyButton}
                      disabled={loading}
                    >
                      No
                    </Button>
                  </Box>
                </Box>
                <Typography
                  sx={{
                    fontWeight: '400',
                    fontSize: 16,
                    lineHeight: '150%',
                    color: '#232B35',
                  }}
                >
                  {singleQuestion.body}
                </Typography>
                <SurveyStats surveyId={singleQuestion?.surveyId} />
                <Box
                  sx={{
                    width: '100%',
                    display: { xxs: 'flex', xs: 'none' },
                    flexDirection: { xxs: 'column', xs: 'row' },
                    justifyContent: 'right',
                    mt: '16px',
                  }}
                >
                  <Button
                    variant={surveyAnswer === 'Yes' ? 'contained' : 'outlined'}
                    color="primary"
                    onClick={() => {
                      setSurveyAnswer('Yes');
                      handleSurveyResponse('Yes');
                    }}
                    sx={classes.surveyButton}
                    disabled={loading}
                  >
                    Yes
                  </Button>
                  <Button
                    variant={surveyAnswer === 'No' ? 'contained' : 'outlined'}
                    color="primary"
                    onClick={() => {
                      setSurveyAnswer('No');
                      handleSurveyResponse('No');
                    }}
                    sx={classes.surveyButton}
                    disabled={loading}
                  >
                    No
                  </Button>
                </Box>
              </CardContent>
            </Card>
          )}
          <Card variant="outlined" sx={{ borderRadius: 2, bgcolor: '#fafcff' }}>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1,
                }}
              >
                <Typography
                  sx={{
                    fontWeight: '600',
                    fontSize: 18,
                    lineHeight: '24px',
                    color: '#232B35',
                  }}
                >
                  Current Priority
                </Typography>
                <Button
                  id="boardroom-share-your-ideas"
                  variant="contained"
                  color="primary"
                  startIcon={<LightbulbIcon />}
                  onClick={() => {
                    if (isFreeTrial && !isFreemium) {
                      eventBus.dispatch(
                        eventBusValues.triggerAdvisorUpgradeAction,
                        {
                          origin: 'submit-idea',
                        }
                      );
                    } else {
                      setShareYourIdeasDialogOpen(true);
                    }
                  }}
                  sx={{
                    textTransform: 'none',
                    bgcolor: '#4FB5F9',
                    borderRadius: '8px',
                    boxShadow: 'none',
                    display: {
                      xxs: 'none',
                      xs: company.currentGoal ? 'flex' : 'none',
                    },
                    width: { xs: 181 },
                    mt: { xs: 0 },
                    height: 36,
                    fontSize: 14,
                    fontWeight: '600',
                    '&:hover': { bgcolor: '#4FB5F9' },
                  }}
                >
                  Share Your Ideas
                </Button>
              </Box>
              <Typography
                sx={{
                  fontWeight: '400',
                  fontSize: 16,
                  lineHeight: '150%',
                  color: '#232B35',
                }}
              >
                {company.currentGoal
                  ? extractUrl(company.currentGoal)
                  : 'No current goal'}
              </Typography>
              <Button
                id="boardroom-share-your-ideas"
                variant="contained"
                color="primary"
                startIcon={<LightbulbIcon />}
                onClick={() => {
                  if (isFreeTrial && !isFreemium) {
                    eventBus.dispatch(
                      eventBusValues.triggerAdvisorUpgradeAction,
                      {
                        origin: 'share-ideas-boardroom',
                      }
                    );
                  } else {
                    setShareYourIdeasDialogOpen(true);
                  }
                }}
                sx={{
                  textTransform: 'none',
                  bgcolor: '#4FB5F9',
                  borderRadius: '8px',
                  boxShadow: 'none',
                  display: {
                    xxs: company.currentGoal ? 'flex' : 'none',
                    xs: 'none',
                  },
                  width: '100%',
                  mt: 1,
                  height: 42,
                  fontSize: 14,
                  fontWeight: '600',
                  '&:hover': { bgcolor: '#4FB5F9' },
                }}
              >
                Share Your Ideas
              </Button>
            </CardContent>
          </Card>
          {nextMeeting && (
            <Card
              variant="outlined"
              sx={{ borderRadius: 2, bgcolor: '#fafcff', cursor: 'pointer' }}
              onClick={(e) => {
                history.push(`/post/${nextMeeting.id}`, {
                  companySlug: company?.slug,
                });
              }}
            >
              <CardContent>
                <Box
                  sx={{
                    display: 'grid',
                    gridAutoFlow: 'column',
                    gridTemplateColumns: {
                      xs: 'minmax(0,1fr) auto',
                      sm: 'minmax(0,1fr) auto auto',
                    },
                    alignItems: 'center',
                    columnGap: '15px',
                    mb: 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: '600',
                      fontSize: 18,
                      lineHeight: '24px',
                      color: '#232B35',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      minWidth: 0,
                    }}
                    title={nextMeeting.postTitle || 'Board Meeting'}
                  >
                    {nextMeeting.postTitle || 'Board Meeting'}
                  </Typography>
                  <Box
                    sx={{
                      backgroundColor: '#2B71F6',
                      color: 'white',
                      textAlign: 'center',
                      border: '1px solid #2B71F6',
                      fontFamily: 'Poppins',
                      fontWeight: 500,
                      borderRadius: isSM ? '15px' : '20px',
                      px: isSM ? '11px' : '15px',
                      py: isSM ? '4px' : '5px',
                      fontSize: isSM ? '12px' : '14px',
                      justifySelf: 'start',
                      flexShrink: 0,
                    }}
                  >
                    {time}
                  </Box>
                  <Button
                    id="boardroom-request-to-speak"
                    variant="contained"
                    color="primary"
                    startIcon={<RecordVoiceOver />}
                    onClick={handleRequestToSpeak}
                    disabled={requestToSpeakLoading}
                    sx={{
                      textTransform: 'none',
                      bgcolor: '#4FB5F9',
                      borderRadius: '8px',
                      boxShadow: 'none',
                      display: {
                        xxs: 'none',
                        xs: time !== 'Replay' ? 'flex' : 'none',
                      },
                      width: { xs: 181 },
                      height: 36,
                      fontSize: 14,
                      fontWeight: '600',
                      '&:hover': { bgcolor: '#4FB5F9' },
                      justifySelf: 'end',
                    }}
                  >
                    Request to Speak
                  </Button>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    mb: 1,
                    gap: '15px',
                  }}
                >
                  <img src={calendarcheck} alt="calendar-check" />
                  <Box>
                    <Typography
                      sx={{
                        fontWeight: '400',
                        fontSize: 16,
                        lineHeight: '24px',
                        color: '#1F5ED7',
                      }}
                    >
                      {formattedLocalDateTime}
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: '400',
                        fontSize: 12,
                        lineHeight: '18px',
                        color: '#4D586F',
                      }}
                    >
                      {formattedLocalMeetingTime}
                    </Typography>
                  </Box>
                </Box>
                <Button
                  id="boardroom-request-to-speak"
                  variant="contained"
                  color="primary"
                  startIcon={<RecordVoiceOver />}
                  disabled={requestToSpeakLoading}
                  onClick={handleRequestToSpeak}
                  sx={{
                    textTransform: 'none',
                    bgcolor: '#4FB5F9',
                    borderRadius: '8px',
                    boxShadow: 'none',
                    display: {
                      xxs: time !== 'Replay' ? 'flex' : 'none',
                      xs: 'none',
                    },
                    width: '100%',
                    mt: 1,
                    height: 42,
                    fontSize: 14,
                    fontWeight: '600',
                    '&:hover': { bgcolor: '#4FB5F9' },
                  }}
                >
                  Request to Speak
                </Button>
              </CardContent>
            </Card>
          )}
        </Stack>
      </Paper>
      <ShareYourIdeasDialog
        open={shareYourIdeasDialogOpen}
        onClose={() => setShareYourIdeasDialogOpen(false)}
        company={company}
      />
      <ConfirmRequestToSpeakDialog
        open={openRequestToSpeakDialog}
        onClose={() => setOpenRequestToSpeakDialog(false)}
      />
    </Box>
  );
}
